import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, PhotoTag, scaleFor, ORANGE } from '../CardBits';

// Template D — Stacked.
// The stacking axis FLIPS: vertical in portrait, horizontal in square and wide.
// It stays distinct from SplitCard because it keeps padding, rounded frames and
// the delta pill BETWEEN the photos rather than bleeding to the edge.
//
// EXPORT SAFETY: no object-fit / clip-path / filter. See lib/photoRender.js.
export default function StackedCard({ model, W, H, form }) {
  const s = scaleFor(H);
  const pad = 40 * s;
  const vertical = form === 'portrait';
  const headerH = vertical ? 130 * s : 96 * s;
  const footerH = 70 * s;
  const pillSize = vertical ? 84 * s : 150 * s;

  const inner = { w: W - pad * 2, h: H - pad * 2 - headerH - footerH };
  // In portrait the height-locked 9:16 aperture is far narrower than the raw
  // pane width (1000 wide slot -> 438 wide aperture at the default 9:16
  // ratio), so most of each pane was blurred backdrop either side of a
  // narrow photo. Cap the pane width to stay near the aperture's own aspect
  // so the pane is mostly photo; the flex container's alignItems:'center'
  // already centres a narrower pane. Square/wide are untouched — the
  // horizontal layout there was never the reported problem.
  const slot = vertical
    ? { w: Math.min(inner.w, ((inner.h - pillSize) / 2) * 0.75), h: (inner.h - pillSize) / 2 }
    : { w: (inner.w - pillSize - 24 * s) / 2, h: inner.h };

  const pane = (side, label, tone) => (
    <div style={{ position: 'relative', width: slot.w, height: slot.h }}>
      <AlignedPhotoFrame
        url={side.url}
        backdropUrl={side.backdropUrl}
        canonical={side.canonical}
        manual={side.manual}
        natural={side.natural}
        slotW={slot.w}
        slotH={slot.h}
        radius={24 * s}
      />
      <PhotoTag tone={tone} s={s} style={{ left: 18 * s, top: 18 * s }}>
        {label}
        {side.dateLabel ? ` · ${side.dateLabel}` : ''}
      </PhotoTag>
      {(side.weightLabel || side.bodyFatLabel) && (
        <PhotoTag tone="dark" s={s} style={{ right: 18 * s, bottom: 18 * s }}>
          {[side.weightLabel, side.bodyFatLabel].filter(Boolean).join(' · ')}
        </PhotoTag>
      )}
    </div>
  );

  const pill = (
    <div
      style={{
        flex: 'none',
        alignSelf: 'center',
        background: ORANGE,
        borderRadius: 999,
        padding: vertical ? `${10 * s}px ${34 * s}px` : `${16 * s}px ${20 * s}px`,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 46 * s, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {model.hero.value}
      </div>
      {model.hero.unit && (
        <div style={{ fontSize: 22 * s, opacity: 0.85, letterSpacing: 2 * s }}>{model.hero.unit.toUpperCase()}</div>
      )}
    </div>
  );

  return (
    <CardShell W={W} H={H} padding={pad} background="#0d0d0f">
      <div
        style={{
          height: headerH,
          display: 'flex',
          flexDirection: vertical ? 'column' : 'row',
          alignItems: vertical ? 'center' : 'baseline',
          justifyContent: 'space-between',
          textAlign: vertical ? 'center' : 'left',
        }}
      >
        <div style={{ fontSize: 62 * s, fontWeight: 800, letterSpacing: 3 * s }}>{model.labels.transformation}</div>
        <div style={{ fontSize: 24 * s, opacity: 0.45, letterSpacing: 3 * s }}>
          {[model.elapsedLabel, model.poseLabel].filter(Boolean).join(' · ').toUpperCase()}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: vertical ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: vertical ? 0 : 24 * s,
          height: inner.h,
        }}
      >
        {pane(model.before, model.labels.before, 'dark')}
        {pill}
        {pane(model.after, model.labels.after, 'accent')}
      </div>

      <div style={{ marginTop: 'auto', paddingTop: 18 * s }}>
        <BrandFooter handleLabel={model.handleLabel} s={s} />
      </div>
    </CardShell>
  );
}
